import { useQuery } from "@tanstack/react-query";
import axios from "axios";

import { getExperimentApiErrorMessage } from "./experimentsApi";
import { getRuntimeConfigFirst } from "../../../../../config/runtimeConfig";
import {
  getCreditCardDemoFeedbackFlow,
  isDemoCreditCardExperimentId,
} from "../utils/demoExperimentData";
import {
  DEFAULT_INTRO_DESCRIPTION,
  DEFAULT_OUTRO_DESCRIPTION,
  normalizeFeedbackFlowContent,
  normalizeFeedbackFlowQuestion,
  normalizeFeedbackFlowTermsForRequest,
  normalizeTaggedTarget,
  sortFeedbackFlowQuestions,
} from "../utils/feedbackFlow";

import type {
  FeedbackFlow,
  FeedbackFlowContent,
  FeedbackFlowQuestion,
  FeedbackFlowQuestionInputType,
  FeedbackFlowQuestionSection,
  FeedbackFlowQuestionSource,
  FeedbackFlowQuestionSentiment,
  TaggedTarget,
} from "../types/experiment.types";

const configuredApiBaseUrl = getRuntimeConfigFirst([
  "VITE_INNOVATION_API_BASE_URL",
  "VITE_API_URL",
]);

const API_BASE_URL_CANDIDATES = [
  configuredApiBaseUrl,
  "https://falcon-api-innovation-dev-ggdcgtajgnhbevb7.centralus-01.azurewebsites.net",
].filter(Boolean) as string[];

export const FEEDBACK_FLOW_QUERY_KEY = ["feedback-flow"];

interface ApiResponseEnvelope<T> {
  success?: boolean;
  statusCode?: number;
  message?: string;
  data?: T | null;
  errors?: string[];
  metadata?: Record<string, unknown> | null;
  timestamp?: string;
}

export interface FeedbackFlowApiContent {
  enabled?: boolean;
  description?: string;
  terms?: string | null;
}

export interface FeedbackFlowApiQuestion {
  id?: string;
  iterationId?: string;
  section?: FeedbackFlowQuestionSection;
  source?: FeedbackFlowQuestionSource;
  premiseId?: string | null;
  question?: string;
  inputType?: FeedbackFlowQuestionInputType;
  options?: string[] | null;
  taggedTarget?: TaggedTarget | null;
  sortOrder?: number | null;
  sentiment?: FeedbackFlowQuestionSentiment | null;
  dislikeReason?: string | null;
}

export interface FeedbackFlowApiData {
  iterationId?: string;
  intro?: FeedbackFlowApiContent | null;
  outro?: FeedbackFlowApiContent | null;
  demographicsEnabled?: boolean;
  personalInfoEnabled?: boolean;
  questions?: FeedbackFlowApiQuestion[] | null;
}

export interface FeedbackFlowQueryData {
  baseUrl: string;
  rawData: FeedbackFlowApiData | null;
  feedbackFlow: FeedbackFlow;
}

export interface UpdateFeedbackFlowSectionsRequest {
  demographicsEnabled?: boolean | null;
  personalInfoEnabled?: boolean | null;
}

export interface CreateFeedbackFlowQuestionRequest {
  section: FeedbackFlowQuestionSection;
  source: FeedbackFlowQuestionSource;
  premiseId?: string | null;
  question: string;
  inputType: FeedbackFlowQuestionInputType;
  options: string[];
  taggedTarget: TaggedTarget | null;
}

export interface UpdateFeedbackFlowQuestionRequest {
  section: FeedbackFlowQuestionSection;
  premiseId?: string | null;
  question: string;
  inputType: FeedbackFlowQuestionInputType;
  options: string[];
  taggedTarget: TaggedTarget | null;
  sentiment?: FeedbackFlowQuestionSentiment | null;
  dislikeReason?: string | null;
}

export interface ReorderFeedbackFlowQuestionsRequest {
  section: FeedbackFlowQuestionSection;
  questionIds: string[];
}

const buildFeedbackFlowBasePath = (
  experimentId: string,
  iterationId: string,
): string =>
  `/api/v1/experiments/${encodeURIComponent(
    experimentId,
  )}/iterations/${encodeURIComponent(iterationId)}/feedback-flow`;

const getCandidateBaseUrls = (preferredBaseUrl?: string): string[] => {
  if (!preferredBaseUrl) {
    return API_BASE_URL_CANDIDATES;
  }

  return [
    preferredBaseUrl,
    ...API_BASE_URL_CANDIDATES.filter((candidate) => candidate !== preferredBaseUrl),
  ];
};

const buildCandidateUrl = (baseUrl: string, path: string) => `${baseUrl}${path}`;

const FEEDBACK_FLOW_QUESTION_HEADERS = {
  Accept: "text/plain",
  "Content-Type": "application/json-patch+json",
};

const FEEDBACK_FLOW_SECTION_HEADERS = {
  Accept: "*/*",
  "Content-Type": "application/json-patch+json",
};

const parsePlainJsonResponse = <T>(data: unknown): T => {
  if (typeof data === "string") {
    return JSON.parse(data) as T;
  }

  return data as T;
};

const runFeedbackFlowRequest = async <T>(
  request: (baseUrl: string) => Promise<T>,
  preferredBaseUrl?: string,
): Promise<{ baseUrl: string; data: T }> => {
  let lastError: unknown;

  for (const baseUrl of getCandidateBaseUrls(preferredBaseUrl)) {
    try {
      const data = await request(baseUrl);
      return { baseUrl, data };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        throw error;
      }

      lastError = error;
    }
  }

  throw lastError ?? new Error("Unable to reach the feedback flow API.");
};

const mapApiQuestionToFeedbackFlowQuestion = (
  question: FeedbackFlowApiQuestion,
  fallbackSortOrder: number,
): FeedbackFlowQuestion =>
  normalizeFeedbackFlowQuestion({
    id: question.id?.trim() || crypto.randomUUID(),
    section: question.section ?? "prototype",
    source: question.source ?? "general",
    premiseId: question.premiseId ?? null,
    question: question.question ?? "",
    inputType: question.inputType ?? "text",
    options: question.options ?? [],
    taggedTarget: normalizeTaggedTarget(question.taggedTarget),
    sortOrder:
      typeof question.sortOrder === "number" && Number.isFinite(question.sortOrder)
        ? question.sortOrder
        : fallbackSortOrder,
    sentiment: question.sentiment ?? null,
    dislikeReason: question.dislikeReason ?? null,
  });

export const mapFeedbackFlowApiDataToModel = (
  data?: FeedbackFlowApiData | null,
): FeedbackFlow => {
  const questions = sortFeedbackFlowQuestions(
    (data?.questions ?? []).map((question, index) =>
      mapApiQuestionToFeedbackFlowQuestion(question, index),
    ),
  );

  return {
    intro: normalizeFeedbackFlowContent(
      data?.intro,
      DEFAULT_INTRO_DESCRIPTION,
    ),
    outro: normalizeFeedbackFlowContent(
      data?.outro,
      DEFAULT_OUTRO_DESCRIPTION,
    ),
    demographicsEnabled:
      typeof data?.demographicsEnabled === "boolean"
        ? data.demographicsEnabled
        : questions.some((question) => question.section === "demographics"),
    personalInfoEnabled:
      typeof data?.personalInfoEnabled === "boolean"
        ? data.personalInfoEnabled
        : questions.some((question) => question.section === "personal_info"),
    questions,
  };
};

export const mapFeedbackFlowContentToRequest = (
  content: FeedbackFlowContent,
): FeedbackFlowApiContent => ({
  enabled: Boolean(content.enabled),
  description: content.description,
  terms: normalizeFeedbackFlowTermsForRequest(content.terms),
});

const mapQuestionOptionsForRequest = (
  inputType: FeedbackFlowQuestionInputType,
  options: string[],
): string[] =>
  normalizeFeedbackFlowQuestion({
    id: crypto.randomUUID(),
    section: "prototype",
    source: "general",
    question: "",
    inputType,
    options,
    taggedTarget: null,
    sortOrder: 0,
    sentiment: null,
    dislikeReason: null,
  }).options;

export const mapFeedbackFlowQuestionToCreateRequest = (
  question: FeedbackFlowQuestion,
): CreateFeedbackFlowQuestionRequest => ({
  section: question.section,
  source: question.source,
  ...(question.section === "prototype"
    ? { premiseId: question.premiseId?.trim() || null }
    : {}),
  question: question.question.trim(),
  inputType: question.inputType,
  options: mapQuestionOptionsForRequest(question.inputType, question.options),
  taggedTarget: normalizeTaggedTarget(question.taggedTarget),
});

export const mapFeedbackFlowQuestionToUpdateRequest = (
  question: FeedbackFlowQuestion,
): UpdateFeedbackFlowQuestionRequest => {
  const shouldPersistReaction = question.source === "ai_generated";

  return {
    section: question.section,
    ...(question.section === "prototype"
      ? { premiseId: question.premiseId?.trim() || null }
      : {}),
    question: question.question.trim(),
    inputType: question.inputType,
    options: mapQuestionOptionsForRequest(question.inputType, question.options),
    taggedTarget: normalizeTaggedTarget(question.taggedTarget),
    sentiment: shouldPersistReaction ? question.sentiment ?? null : null,
    dislikeReason:
      shouldPersistReaction && question.sentiment === "dislike"
        ? normalizeFeedbackFlowTermsForRequest(question.dislikeReason)
        : null,
  };
};

export const getFeedbackFlow = async (
  experimentId: string,
  iterationId: string,
  baseUrl?: string,
): Promise<FeedbackFlowQueryData> => {
  // Demo interception: the Credit Card Streamlining demo has no API
  // backing; return its hardcoded feedback flow so the Tags + Finalize
  // tabs render with the same 12 interview questions used in insights.
  if (isDemoCreditCardExperimentId(experimentId)) {
    return {
      baseUrl: "demo",
      rawData: null,
      feedbackFlow: getCreditCardDemoFeedbackFlow(),
    };
  }

  const feedbackFlowPath = buildFeedbackFlowBasePath(experimentId, iterationId);
  const { baseUrl: resolvedBaseUrl, data } = await runFeedbackFlowRequest(
    async (candidateBaseUrl) => {
      const response = await axios.get<ApiResponseEnvelope<FeedbackFlowApiData>>(
        `${candidateBaseUrl}${feedbackFlowPath}`,
      );
      return response.data.data ?? null;
    },
    baseUrl,
  );

  return {
    baseUrl: resolvedBaseUrl,
    rawData: data,
    feedbackFlow: mapFeedbackFlowApiDataToModel(data),
  };
};

export const patchFeedbackFlowIntro = async (
  experimentId: string,
  iterationId: string,
  content: FeedbackFlowContent,
  baseUrl?: string,
): Promise<{ baseUrl: string }> => {
  const path = `${buildFeedbackFlowBasePath(experimentId, iterationId)}/intro`;

  const result = await runFeedbackFlowRequest(
    async (candidateBaseUrl) => {
      await axios.patch(
        buildCandidateUrl(candidateBaseUrl, path),
        mapFeedbackFlowContentToRequest(content),
      );
      return null;
    },
    baseUrl,
  );

  return { baseUrl: result.baseUrl };
};

export const patchFeedbackFlowOutro = async (
  experimentId: string,
  iterationId: string,
  content: FeedbackFlowContent,
  baseUrl?: string,
): Promise<{ baseUrl: string }> => {
  const path = `${buildFeedbackFlowBasePath(experimentId, iterationId)}/outro`;

  const result = await runFeedbackFlowRequest(
    async (candidateBaseUrl) => {
      await axios.patch(
        buildCandidateUrl(candidateBaseUrl, path),
        mapFeedbackFlowContentToRequest(content),
      );
      return null;
    },
    baseUrl,
  );

  return { baseUrl: result.baseUrl };
};

export const patchFeedbackFlowSections = async (
  experimentId: string,
  iterationId: string,
  request: UpdateFeedbackFlowSectionsRequest,
  baseUrl?: string,
): Promise<{ baseUrl: string }> => {
  const path = `${buildFeedbackFlowBasePath(experimentId, iterationId)}/sections`;

  const result = await runFeedbackFlowRequest(
    async (candidateBaseUrl) => {
      await axios.patch(buildCandidateUrl(candidateBaseUrl, path), request, {
        headers: FEEDBACK_FLOW_SECTION_HEADERS,
      });
      return null;
    },
    baseUrl,
  );

  return { baseUrl: result.baseUrl };
};

export const createFeedbackFlowQuestion = async (
  experimentId: string,
  iterationId: string,
  question: FeedbackFlowQuestion,
  baseUrl?: string,
): Promise<{ baseUrl: string; question: FeedbackFlowQuestion }> => {
  const path = `${buildFeedbackFlowBasePath(experimentId, iterationId)}/questions`;

  const { baseUrl: resolvedBaseUrl, data } = await runFeedbackFlowRequest(
    async (candidateBaseUrl) => {
      const response = await axios.post<ApiResponseEnvelope<FeedbackFlowApiQuestion>>(
        buildCandidateUrl(candidateBaseUrl, path),
        mapFeedbackFlowQuestionToCreateRequest(question),
        {
          headers: FEEDBACK_FLOW_QUESTION_HEADERS,
        },
      );
      return (
        parsePlainJsonResponse<ApiResponseEnvelope<FeedbackFlowApiQuestion> | null>(
          response.data,
        )?.data ?? null
      );
    },
    baseUrl,
  );
  const persistedQuestion = mapApiQuestionToFeedbackFlowQuestion(
    data ?? {},
    question.sortOrder,
  );

  return {
    baseUrl: resolvedBaseUrl,
    question: normalizeFeedbackFlowQuestion({
      ...persistedQuestion,
      premiseId: persistedQuestion.premiseId ?? question.premiseId ?? null,
    }),
  };
};

export const updateFeedbackFlowQuestion = async (
  experimentId: string,
  iterationId: string,
  question: FeedbackFlowQuestion,
  baseUrl?: string,
): Promise<{ baseUrl: string }> => {
  const path = `${buildFeedbackFlowBasePath(
    experimentId,
    iterationId,
  )}/questions/${encodeURIComponent(question.id)}`;

  const result = await runFeedbackFlowRequest(
    async (candidateBaseUrl) => {
      await axios.patch(
        buildCandidateUrl(candidateBaseUrl, path),
        mapFeedbackFlowQuestionToUpdateRequest(question),
        {
          headers: FEEDBACK_FLOW_QUESTION_HEADERS,
        },
      );
      return null;
    },
    baseUrl,
  );

  return { baseUrl: result.baseUrl };
};

export const deleteFeedbackFlowQuestion = async (
  experimentId: string,
  iterationId: string,
  questionId: string,
  baseUrl?: string,
): Promise<{ baseUrl: string }> => {
  const path = `${buildFeedbackFlowBasePath(
    experimentId,
    iterationId,
  )}/questions/${encodeURIComponent(questionId)}`;

  const result = await runFeedbackFlowRequest(
    async (candidateBaseUrl) => {
      await axios.delete(buildCandidateUrl(candidateBaseUrl, path));
      return null;
    },
    baseUrl,
  );

  return { baseUrl: result.baseUrl };
};

export const reorderFeedbackFlowQuestions = async (
  experimentId: string,
  iterationId: string,
  request: ReorderFeedbackFlowQuestionsRequest,
  baseUrl?: string,
): Promise<{ baseUrl: string }> => {
  const path = `${buildFeedbackFlowBasePath(experimentId, iterationId)}/questions/order`;

  const result = await runFeedbackFlowRequest(
    async (candidateBaseUrl) => {
      await axios.patch(buildCandidateUrl(candidateBaseUrl, path), request);
      return null;
    },
    baseUrl,
  );

  return { baseUrl: result.baseUrl };
};

export const useFeedbackFlow = (
  experimentId?: string,
  iterationId?: string | null,
  options?: {
    enabled?: boolean;
    baseUrl?: string;
  },
) => {
  const isEnabled =
    Boolean(experimentId) &&
    Boolean(iterationId) &&
    Boolean(options?.enabled ?? true);

  return useQuery({
    queryKey: [
      ...FEEDBACK_FLOW_QUERY_KEY,
      options?.baseUrl ?? "default",
      experimentId,
      iterationId,
    ],
    queryFn: async () =>
      getFeedbackFlow(experimentId as string, iterationId as string, options?.baseUrl),
    enabled: isEnabled,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export const getFeedbackFlowErrorMessage = (
  error: unknown,
  fallback: string,
): string => getExperimentApiErrorMessage(error, fallback);
